import { QueryClient } from '@tanstack/react-query';
import { shouldRetryQuery } from '../utils/graphqlClient';

/**
 * Creates a configured QueryClient with sensible defaults for the application.
 *
 * Features:
 * - Consistent retry logic that doesn't retry on authorization errors
 * - Default stale time of 5 minutes for queries
 * - Garbage collection time of 10 minutes for unused cache entries
 * - Global error handling for mutations
 * - Optimized refetch behavior
 */
export const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes - unused data stays in cache for 10 minutes (formerly cacheTime)
        retry: shouldRetryQuery, // Use centralized retry logic
        refetchOnWindowFocus: false, // Don't refetch when window regains focus
        refetchOnReconnect: true, // Refetch when network reconnects
        refetchOnMount: true, // Refetch when component mounts (if data is stale)
      },
      mutations: {
        retry: false, // Mutations typically shouldn't retry automatically
        onError: error => {
          // Global error handling for mutations
          // In development, log errors for debugging
          if (import.meta.env.DEV) {
            console.error('Mutation error:', error);
          }
          // TODO: Integrate with error tracking service (e.g., Sentry) in production
        },
      },
    },
  });
};
